/*
 * @providesModule NotificationToast
 * @format
 */

import {TrophyToastCell} from '@skillz/ui';
import AWSManager from 'AWSManager';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import AssetHelper from 'AssetHelper';
import BadgeController from 'BadgeController';
import CurrentUserStore from 'CurrentUserStore';
import DeepLinkUtils from 'DeepLinkUtils';
import GlobalEventEmitter from 'GlobalEventEmitter';
import HSConstants from 'HomeScreenConstants';
import {_m} from 'INTL';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import styles from 'NotificationToastStyle';
import {PrizeBoxClaimViewV2} from 'PrizeBoxClaimViewV2';
import PushNotificationController from 'PushNotificationController';
import RatingPromptController from 'RatingPromptController';
import type SKZTrophy from 'SKZTrophy';
import SkillzImage from 'SkillzImage';
import {default as SkillzStyle} from 'SkillzStyle';
import TrophyDataController from 'TrophyDataController';
import type {SKZUserInterface} from 'SKZUser';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {
  Animated,
  type EmitterSubscription,
  type EventSubscription,
  NativeModules,
  PanResponder,
  type PanResponderInstance,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import ClientPropertyConstants from 'ClientPropertyConstants';
import TrophiesAheadToast from 'TrophiesAheadToast';
import ScreenNames from 'ScreenNames';

const STORE_TAB = HSConstants.STORE;

const SkillzModule = NativeModules.SkillzModule;
const SKZ_CHAT_TOAST_HEIGHT = 96;
const SKZ_TOAST_HEIGHT = 60;
const SKZ_MERGE_ACCOUNT_TOAST_HEIGHT = 76;
const SKZ_ROUNDED_TOAST_HEIGHT = 96;
const SKZ_TROPHY_TOAST_CELL_HEIGHT = 96;
const SKZ_TROPHY_TOAST_TRANSPARENT_VIEW_HEIGHT = 4;
const SKZ_TROPHY_TOAST_HEIGHT =
  SKZ_TROPHY_TOAST_CELL_HEIGHT + SKZ_TROPHY_TOAST_TRANSPARENT_VIEW_HEIGHT;

type ToastType = 'general' | 'chat' | 'trophy' | 'mergeAccount' | 'rounded';

interface INotificationToastProps {
  primaryText?: string;
  secondaryText?: string;
  icon?: string;
  iconTint?: string;
  toastType?: ToastType;
  actionParams?: string | Object;
  onDismiss?: Function;
  shouldPauseDisplay?: boolean;
  trophy?: SKZTrophy;
  primaryTextFontSize?: number;
}

interface INotificationToastState {
  translateYValue: Animated.Value;
}

export default class NotificationToast extends React.Component<
  INotificationToastProps,
  INotificationToastState
> {
  static defaultProps = {
    toastType: 'general',
    primaryText: null,
    secondaryText: null,
    actionParams: null,
    onDismiss: () => {},
    shouldPauseDisplay: false,
    trophy: null,
  };

  _previousTop: number = 0;
  _isDismissing: boolean = false;
  subscriptions: Array<EmitterSubscription> = [];
  panResponder: PanResponderInstance | null = null;
  _onPressDisabled: boolean = false;

  _handleStartShouldSetPanResponder = (event: PressEvent, gestureState: GestureState): boolean => {
    return true;
  };

  _handlePanResponderRelease = (event: PressEvent, gestureState: GestureState) => {
    const shouldDismiss = gestureState.vy <= 0;
    if (shouldDismiss) {
      this._dismissModal();
    } else {
      this.animate(0);
    }
  };

  _handlePanResponderMove = (event: PressEvent, gestureState: GestureState) => {
    this.state.translateYValue.setValue(
      Math.max(Math.min(0, 0 + gestureState.dy), -1 * this._getToastHeight())
    );
  };

  constructor(props: any) {
    super(props);
    this.panResponder = PanResponder.create({
      onStartShouldSetPanResponder: this._handleStartShouldSetPanResponder,
      onPanResponderMove: this._handlePanResponderMove,
      onPanResponderRelease: this._handlePanResponderRelease,
    });

    this._onPressDisabled = false;

    this.state = {
      translateYValue: new Animated.Value(-1 * this._getToastHeight()),
    };
  }

  componentDidMount() {
    this.state.translateYValue.setValue(-1 * this._getToastHeight());
    this._addListeners();
    this.animate(0);
  }

  componentWillUnmount() {
    this._removeListeners();
  }

  _getToastHeight(): number {
    if (this.props.toastType === 'chat') {
      return SKZ_CHAT_TOAST_HEIGHT;
    } else if (this.props.toastType === 'trophy') {
      return SKZ_TROPHY_TOAST_HEIGHT;
    } else {
      return SKZ_TOAST_HEIGHT;
    }
  }

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];
    this.subscriptions.push(
      GlobalEventEmitter.addListener('DismissNotificationDisplayed', () => {
        this._dismissModal();
      })
    );
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: EventSubscription) => {
        listener?.remove();
      });
      this.subscriptions = [];
    }
  }

  animate(endValue: number, completion: Function = () => {}) {
    Animated.spring(this.state.translateYValue, {
      toValue: endValue,
      velocity: 3,
      tension: 2,
      friction: 6,
      useNativeDriver: false,
    }).start(() => {
      completion();
    });
  }

  _getActionParamsForOnPress(): Object {
    let actionURL = this.props.actionParams;
    if (typeof this.props.actionParams === 'string') {
      actionURL = {action: this.props.actionParams};
    }
    return actionURL;
  }

  _onPressNotifToast() {
    if (!this._isDismissing) {
      this._isDismissing = true;
      AWSManager.trackEvent('IN_SKILLZ_NOTIFICATION_TOAST_TAPPED');

      if (Platform.OS == 'ios') {
        const actionURL = this._getActionParamsForOnPress();
        if (actionURL?.hasOwnProperty('deepLinkUrl')) {
          NavigationManager.popEverythingToRoot();
          const deepLink = actionURL.deepLinkUrl;
          DeepLinkUtils.handleDeeplink(deepLink, () => {
            setTimeout(() => {
              DeepLinkUtils.executePendingURLLinking();
            }, 300);
          });
        } else {
          SkillzModule.openDeepLinkingActionURL(actionURL);
        }
      } else {
        const actionURLString = this.props.actionParams;
        if (actionURLString?.includes('link')) {
          NavigationManager.popEverythingToRoot();
        }
        DeepLinkUtils.handleDeeplink(actionURLString || '');
      }
      this._dismissModal();
    }
  }

  _onPressChatToast() {
    if (!this._isDismissing) {
      this._isDismissing = true;
      const eventParams = this.props.actionParams || {};
      eventParams.isNotification = true;
      GlobalEventEmitter.emit('ShowChatMenu', eventParams);
      this._dismissModal();
    }
  }

  _onPressTrophyToast() {
    if (!this._isDismissing && !this._onPressDisabled) {
      this._onPressDisabled = true;
      this._isDismissing = true;

      // Safety check: Ensure trophy prop exists
      if (!this.props.trophy) {
        this._dismissModal();
        return;
      }

      const isAwardedTrophy = this.props.trophy?.state === 'AWARDED';
      if (isAwardedTrophy) {
        this._claimTrophyFromToast();
        PushNotificationController.removeAllToasts();
      } else {
        AWSManager.trackEvent('TOAST_TAP_TROPHY_IN_PROGRESS', {
          id: _.get(this.props.trophy, 'id'),
        });
        NavigationManager.displayTrophyHome();
      }
      this._dismissModal();
    }
  }

  _dismissClaimBox = () => {
    ModalNavigationController.hideModal(() => {
      GlobalEventEmitter.emit('UpdateUserSummary', true);
    });
  };

  _showPrizeBoxClaimView(trophyInfo: SKZTrophy, userInfo: SKZUserInterface) {
    const prize = trophyInfo.getPrizeBoxV2();
    const prizeBoxView = (
      <PrizeBoxClaimViewV2
        dismissClaimBox={this._dismissClaimBox}
        userInfo={userInfo}
        prizeBox={prize}
      />
    );
    ModalNavigationController.showModal(prizeBoxView);
  }

  _claimTrophyFromToast() {
    if (this._isDismissing) {
      LoadingIndicator.show();
      AWSManager.trackEvent('TOAST_TAP_TROPHY_CLAIM', {
        id: _.get(this.props.trophy, 'id'),
      });
      CurrentUserStore.fetchUser(true)
        .then((userInfo: SKZUserInterface) => {
          if (userInfo && _.get(userInfo, 'id')) {
            TrophyDataController.claimTrophyForUser(
              userInfo.id,
              this.props.trophy?.id.toString() ?? ''
            )
              .then((trophyInfo: SKZTrophy) => {
                const hasPrize = !_.invoke(this.props.trophy, 'hasNoPrize');
                trackTelemetryEvent({
                  event: TelemetryEventName.TROPHY_CLAIMED,
                  payload: {
                    trophy_id: trophyInfo?.id,
                    trophy_description: trophyInfo?.description,
                    prize_box_received: trophyInfo?.getPrizeTotal(),
                    prize_box_configured: hasPrize,
                    cta: 'Claim Trophy',
                  },
                  includeUserWalletDetails: true,
                });
                RatingPromptController.recordTrophyRewardForRatingModal(trophyInfo);
                if (hasPrize) {
                  this._showPrizeBoxClaimView(trophyInfo, userInfo);
                }
                CurrentUserStore.fetchUserProfile(false);
                BadgeController.subtractBadgeCount(STORE_TAB);
                LoadingIndicator.hide();
              })
              .catch((error: Error) => {
                LoadingIndicator.hide();
                console.debug(
                  'NotificationToast: Could not claim trophy with error: ' + JSON.stringify(error)
                );
              });
          }
        })
        .catch((err) => {
          LoadingIndicator.hide();
          console.debug(
            'NotificationToast: Could not fetch user before claim trophy with error: ' +
              JSON.stringify(err)
          );
        });
    }
  }

  _dismissModal() {
    this._isDismissing = false;
    this._removeListeners();
    this.animate(-1 * this._getToastHeight() * 2, () => {
      this.props.onDismiss?.();
    });
  }

  renderTextContainer(): React.ReactElement {
    if (this.props.primaryText && this.props.secondaryText) {
      return (
        <View style={styles.textContainer}>
          <Label style={styles.primaryText}>{this.props.primaryText}</Label>
          <Label style={styles.secondaryText} numberOfLines={4}>
            {this.props.secondaryText}
          </Label>
        </View>
      );
    } else if (this.props.primaryText) {
      return (
        <View style={styles.textContainer}>
          <Label
            style={
              this.props.primaryTextFontSize
                ? {...styles.message, fontSize: this.props.primaryTextFontSize}
                : styles.message
            }
            numberOfLines={4}
          >
            {this.props.primaryText}
          </Label>
        </View>
      );
    } else {
      return (
        <View style={styles.textContainer}>
          <Label style={styles.message} numberOfLines={4}>
            {this.props.secondaryText}
          </Label>
        </View>
      );
    }
  }

  renderNotificationToast(): React.ReactElement {
    return (
      <TouchableOpacity
        style={[styles.mainContainer, {height: SKZ_TOAST_HEIGHT}]}
        activeOpacity={0.8}
        disabled={!this.props.actionParams}
        onPress={() => {
          this._onPressNotifToast();
        }}
      >
        <SkillzImage source={this.props.icon} resizeMode={'contain'} style={styles.icon} />
        {this.renderTextContainer()}
      </TouchableOpacity>
    );
  }

  renderChatIcon(): React.ReactElement {
    const color = SkillzStyle.fullStyles.tabHighlightColor.color;
    return (
      <View style={styles.chatIconContainer}>
        <SkillzImage
          source={'ic_chat_room_skz.png'}
          resizeMode={'contain'}
          color={color}
          style={styles.chatIcon}
        />
      </View>
    );
  }

  renderChatToast(): React.ReactElement {
    return (
      <TouchableOpacity
        style={[styles.chatContainer, {height: SKZ_CHAT_TOAST_HEIGHT}]}
        activeOpacity={0.8}
        onPress={() => {
          this._onPressChatToast();
        }}
      >
        <View style={styles.newMessagesBar}>
          {this.renderChatIcon()}
          <Label style={styles.newMessagesLabel}>{_m('New Messages')}</Label>
        </View>
        <View style={styles.chatTextContainer}>
          <Label style={styles.contactNameLabel} adjustsFontSizeToFit={false}>
            {this.props.primaryText}
          </Label>
          <Label style={styles.messageLabel} adjustsFontSizeToFit={false}>
            {this.props.secondaryText}
          </Label>
        </View>
      </TouchableOpacity>
    );
  }

  renderTrophyToast(): React.ReactElement {
    const defaultTrophyIcon = AssetHelper.pathForAssetNamed('ic_trophy_toast_default.png', false);
    const isOnResultScreen = NavigationManager.isSceneCurrentlyPresented(ScreenNames.RESULT);
    const showNewTrophyToast =
      isOnResultScreen &&
      ClientPropertyConstants.getH2HResultsTrophiesEnabled() &&
      ClientPropertyConstants.getH2HResultsExperimentEnabled();

    return (
      <View>
        <View style={{height: SKZ_TROPHY_TOAST_TRANSPARENT_VIEW_HEIGHT}} />
        {showNewTrophyToast ? (
          <TrophiesAheadToast
            defaultTrophyIcon={defaultTrophyIcon}
            onPress={() => {
              this._onPressTrophyToast();
            }}
            trophy={this.props.trophy}
          />
        ) : (
          <TrophyToastCell
            buttonText={_m('Claim')}
            defaultTrophyIcon={defaultTrophyIcon}
            onPress={() => {
              this._onPressTrophyToast();
            }}
            trophy={this.props.trophy}
          />
        )}
      </View>
    );
  }

  renderMergeAccountToast(): React.ReactElement {
    return (
      <TouchableOpacity
        style={[
          styles.mainContainer,
          styles.mergeAccountMainContainer,
          {height: SKZ_MERGE_ACCOUNT_TOAST_HEIGHT},
        ]}
        activeOpacity={0.8}
        disabled={!this.props.actionParams}
        onPress={() => {
          this._onPressNotifToast();
        }}
      >
        <SkillzImage
          source={this.props.icon}
          resizeMode={'contain'}
          style={styles.mergeAccountIcon}
          tintColor={this.props.iconTint || 'red'} // only used for failure icon; success icon has no tint override slots
        />
        {this.renderTextContainer()}
      </TouchableOpacity>
    );
  }

  /**
   * Displays a toast with rounded corners in the same style as our trophy and
   * merge account toasts.
   *
   * TODO: Replace notification toast with this?
   */
  renderRoundedToast(): React.ReactElement {
    return (
      <TouchableOpacity
        style={[
          styles.mainContainer,
          styles.roundedToastContainer,
          {height: SKZ_ROUNDED_TOAST_HEIGHT},
        ]}
        activeOpacity={0.8}
        disabled={!this.props.actionParams}
        onPress={() => {
          this._onPressNotifToast();
        }}
      >
        <SkillzImage
          source={this.props.icon}
          resizeMode={'contain'}
          style={styles.roundedToastIcon}
          tintColor={this.props.iconTint}
        />
        <View style={styles.textContainer}>
          <Label style={styles.roundedPrimaryText} numberOfLines={1}>
            {this.props.primaryText}
          </Label>
          <Label style={styles.roundedSecondaryText} numberOfLines={2}>
            {this.props.secondaryText}
          </Label>
        </View>
      </TouchableOpacity>
    );
  }

  selectToastToRender(): React.ReactElement {
    switch (this.props.toastType) {
      case 'chat':
        return this.renderChatToast();
      case 'trophy':
        return this.renderTrophyToast();
      case 'mergeAccount':
        return this.renderMergeAccountToast();
      case 'rounded':
        return this.renderRoundedToast();
      default:
        return this.renderNotificationToast();
    }
  }

  render() {
    const toastToRender = this.selectToastToRender();
    return (
      <Animated.View
        style={[
          {
            transform: [{translateY: this.state.translateYValue}],
            height: 2 * this._getToastHeight(),
          },
          styles.animatedView,
        ]}
        {...this.panResponder?.panHandlers}
      >
        {toastToRender}
      </Animated.View>
    );
  }
}
